(() => {
  const selectors = {
    optionsRoot: '[data-cart-delivery-options]',
    deliveryMethod: 'input[name="attributes[delivery_method]"]',
    deliveryOption: '[data-delivery-option]',
    deliveryOptionPrice: '[data-delivery-option-price]',
    deliveryPanel: '[data-delivery-panel]',
    pickupAddressInput: '[data-pickup-address-input]',
    pickupCarrierInput: '[data-pickup-carrier-input]',
    checkoutAddressInput: '[data-checkout-address1-input]',
    checkoutButton: 'button[name="checkout"]',
    cartErrors: '#cart-errors',
    cartProductsTotal: '[data-cart-products-total]',
    cartDeliveryTotal: '[data-cart-delivery-total]',
    cartGrandTotal: '[data-cart-grand-total]',
    freeShippingProgress: '[data-free-shipping-progress]',
    freeShippingMessage: '[data-free-shipping-message]',
    freeShippingBar: '[data-free-shipping-bar]',
    pickupAddress: '.pp-address',
    pickupCarrier: '.pp-carrier-name',
  };

  const state = {
    observedPickupSection: null,
    pickupObserver: null,
    persistTimer: null,
    lastPersistedSignature: '',
    syncQueued: false,
    mapPopupWrapped: false,
    mapPopupWrapTimer: null,
    mapZoomTimer: null,
    checkoutUnlockTimer: null,
    leafletHookApplied: false,
    mapCenter: {
      lat: 42.6977,
      lng: 23.3219,
    },
    mapZoomLevel: 14,
  };

  const defaults = {
    sofiaCenter: {
      lat: 42.6977,
      lng: 23.3219,
    },
  };

  const getOptionsRoot = () => document.querySelector(selectors.optionsRoot);

  const getSelectedMethod = () => {
    const checkedOption = document.querySelector(`${selectors.optionsRoot} ${selectors.deliveryMethod}:checked`);
    return checkedOption ? checkedOption.value : 'home_delivery';
  };

  const getSelectedOptionInput = () => {
    return document.querySelector(`${selectors.optionsRoot} ${selectors.deliveryMethod}:checked`);
  };

  const getPickupAddressInput = () => getOptionsRoot()?.querySelector(selectors.pickupAddressInput);

  const getPickupCarrierInput = () => getOptionsRoot()?.querySelector(selectors.pickupCarrierInput);

  const getCheckoutAddressInput = () => getOptionsRoot()?.querySelector(selectors.checkoutAddressInput);

  const getCartErrors = () => document.querySelector(selectors.cartErrors);

  const isBgLanguage = () => getOptionsRoot()?.dataset.isBgLanguage === 'true';
  const getFreeShippingThresholdCents = () => Number(getOptionsRoot()?.dataset.freeShippingThreshold || 0);
  const getPickupRequiredMessage = () =>
    getOptionsRoot()?.dataset.pickupRequiredMessage || 'Please select a pickup point before continuing to checkout.';
  const getCheckoutErrorMessage = () =>
    getOptionsRoot()?.dataset.checkoutErrorMessage || 'We could not continue to checkout. Please try again.';

  const decodeHtmlEntities = (value) => {
    if (!value) return '';

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };

  const formatAmount = (value, decimals, thousandsSeparator, decimalSeparator) => {
    const fixedAmount = value.toFixed(decimals);
    const [whole, fraction] = fixedAmount.split('.');
    const wholeWithSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    if (decimals === 0) return wholeWithSeparators;
    return `${wholeWithSeparators}${decimalSeparator}${fraction}`;
  };

  const formatMoneyAmount = (cents) => {
    const optionsRoot = getOptionsRoot();
    const shopMoneyFormat = optionsRoot?.dataset.shopMoneyFormat || '${{amount}}';
    const amount = Number(cents || 0) / 100;

    const tokenMatch = shopMoneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const token = tokenMatch ? tokenMatch[1] : 'amount';

    switch (token) {
      case 'amount':
        return formatAmount(amount, 2, ',', '.');
      case 'amount_no_decimals':
        return formatAmount(amount, 0, ',', '.');
      case 'amount_with_comma_separator':
        return formatAmount(amount, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatAmount(amount, 0, '.', ',');
      case 'amount_with_space_separator':
        return formatAmount(amount, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return formatAmount(amount, 0, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return formatAmount(amount, 2, "'", '.');
      default:
        return formatAmount(amount, 2, ',', '.');
    }
  };

  const formatMoney = (cents) => {
    const optionsRoot = getOptionsRoot();
    const shopMoneyFormat = optionsRoot?.dataset.shopMoneyFormat || '${{amount}}';

    return shopMoneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, token) => {
      return formatMoneyAmount(cents);
    });
  };

  const formatMoneyWithBgn = (cents, renderParens = true) => {
    if (!isBgLanguage()) return formatMoney(cents);

    const bgnCents = Math.round((Number(cents || 0) * 195583) / 100000);
    const eurMoney = formatMoney(cents);
    const bgnMoney = `${formatMoneyAmount(bgnCents)} лв`;
    const bgnContent = renderParens ? `(${bgnMoney})` : bgnMoney;

    return `${eurMoney} <span class="bgn-value">${bgnContent}</span>`;
  };

  const getFreeLabel = () => getOptionsRoot()?.dataset.freeLabel || 'Free';

  const getCartTotalCents = () => Number(getOptionsRoot()?.dataset.cartTotal || 0);

  const getOptionDeliveryPriceCents = (optionInput) => {
    if (!optionInput) return 0;

    const basePrice = Number(optionInput.dataset.deliveryPrice || 0);
    const freeThreshold = Number(optionInput.dataset.deliveryFreeThreshold || 0);
    const cartTotal = getCartTotalCents();

    if (freeThreshold > 0 && cartTotal >= freeThreshold) return 0;
    return basePrice;
  };

  const updateOptionPrices = () => {
    document.querySelectorAll(`${selectors.optionsRoot} ${selectors.deliveryOption}`).forEach((optionInput) => {
      const optionPrice = optionInput.closest('.cart-delivery-options__option')?.querySelector(selectors.deliveryOptionPrice);
      if (!optionPrice) return;

      const deliveryPriceCents = getOptionDeliveryPriceCents(optionInput);
      optionPrice.innerHTML = deliveryPriceCents === 0 ? getFreeLabel() : formatMoneyWithBgn(deliveryPriceCents, true);
    });
  };

  const updateTotals = () => {
    const cartProductsTotal = document.querySelector(selectors.cartProductsTotal);
    const cartDeliveryTotal = document.querySelector(selectors.cartDeliveryTotal);
    const cartGrandTotal = document.querySelector(selectors.cartGrandTotal);
    const cartTotalCents = getCartTotalCents();
    const selectedOption = getSelectedOptionInput();
    const deliveryPriceCents = getOptionDeliveryPriceCents(selectedOption);

    if (cartProductsTotal) {
      cartProductsTotal.textContent = formatMoney(cartTotalCents);
    }
    if (cartDeliveryTotal) {
      cartDeliveryTotal.textContent = deliveryPriceCents === 0 ? getFreeLabel() : formatMoney(deliveryPriceCents);
    }
    if (cartGrandTotal) {
      cartGrandTotal.innerHTML = formatMoneyWithBgn(cartTotalCents + deliveryPriceCents, false);
    }
  };

  const updateFreeShippingProgress = () => {
    const progressRoot = document.querySelector(selectors.freeShippingProgress);
    const messageElement = document.querySelector(selectors.freeShippingMessage);
    const barElement = document.querySelector(selectors.freeShippingBar);
    const thresholdCents = getFreeShippingThresholdCents();

    if (!progressRoot || !messageElement || !barElement) return;

    if (thresholdCents <= 0) {
      progressRoot.hidden = true;
      return;
    }

    progressRoot.hidden = false;

    const cartTotalCents = getCartTotalCents();
    const remainingCents = Math.max(thresholdCents - cartTotalCents, 0);
    const progressValue = Math.min((cartTotalCents / thresholdCents) * 100, 100);
    const messageTemplate = decodeHtmlEntities(getOptionsRoot()?.dataset.freeShippingMessageTemplate || '');
    const qualifiedMessage = decodeHtmlEntities(getOptionsRoot()?.dataset.freeShippingQualifiedMessage || '');

    messageElement.innerHTML =
      remainingCents > 0
        ? messageTemplate.replace('__amount__', formatMoney(remainingCents))
        : qualifiedMessage;
    barElement.style.width = `${progressValue}%`;
  };

  const updatePickupWidgetTranslations = () => {
    const pickupSection = getPickupSection();
    const selectedLabel = getOptionsRoot()?.dataset.pickupSelectedLabel;
    const changeLabel = getOptionsRoot()?.dataset.pickupChangeLabel;

    if (!pickupSection) return;

    const selectedLabelElement = pickupSection.querySelector('.selected-pp-label');
    const changeLabelElement = pickupSection.querySelector('.change-selected-pp-button');

    if (selectedLabelElement && selectedLabel && selectedLabelElement.textContent !== selectedLabel) {
      selectedLabelElement.textContent = selectedLabel;
    }

    if (changeLabelElement && changeLabel && changeLabelElement.textContent !== changeLabel) {
      changeLabelElement.textContent = changeLabel;
    }
  };

  const getPickupSection = () => {
    return getOptionsRoot()?.closest('.shopify-section')?.querySelector('[data-cart-pickup-widget]') || null;
  };

  const shouldOverridePickupMapCenter = (args) => {
    const latitude = args[2];
    const longitude = args[3];
    const country = args[5];

    return !latitude && !longitude && country === 'BG';
  };

  const getFallbackPickupMapArgs = (args) => {
    const fallbackArgs = [...args];
    fallbackArgs[2] = defaults.sofiaCenter.lat;
    fallbackArgs[3] = defaults.sofiaCenter.lng;
    return fallbackArgs;
  };

  const hookLeafletMaps = () => {
    if (state.leafletHookApplied) return;
    if (!window.L?.Map || typeof window.L.Map.addInitHook !== 'function') return;

    if (!window.__leafletMaps) {
      window.__leafletMaps = [];
    }

    window.L.Map.addInitHook(function () {
      window.__leafletMaps.push(this);
      window.__lastLeafletMap = this;
    });

    state.leafletHookApplied = true;
  };

  const nudgePickupMapZoom = () => {
    window.clearTimeout(state.mapZoomTimer);

    state.mapZoomTimer = window.setTimeout(() => {
      const latestMap = window.__lastLeafletMap;

      if (latestMap && typeof latestMap.setZoom === 'function') {
        latestMap.setZoom(state.mapZoomLevel);
        return;
      }

      const zoomInButtons = Array.from(document.querySelectorAll('.leaflet-control-zoom-in')).filter((button) => {
        return button instanceof HTMLElement && button.offsetParent !== null;
      });

      const visibleZoomInButton = zoomInButtons.at(-1);
      if (!(visibleZoomInButton instanceof HTMLElement)) return;

      visibleZoomInButton.click();
      window.setTimeout(() => {
        visibleZoomInButton.click();
      }, 180);
    }, 300);
  };

  const openPickupMapWithResolvedCenter = (originalPopup, args) => {
    if (!shouldOverridePickupMapCenter(args)) {
      return originalPopup(...args);
    }

    const fallbackArgs = getFallbackPickupMapArgs(args);

    if (!('geolocation' in navigator)) {
      return originalPopup(...fallbackArgs);
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        updateMapCenterFromPosition(position);

        const resolvedArgs = [...fallbackArgs];
        resolvedArgs[2] = position.coords.latitude;
        resolvedArgs[3] = position.coords.longitude;
        originalPopup(...resolvedArgs);
        nudgePickupMapZoom();
      },
      () => {
        originalPopup(...fallbackArgs);
        nudgePickupMapZoom();
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000,
      }
    );

    return undefined;
  };

  const wrapPickupMapPopup = () => {
    if (state.mapPopupWrapped || typeof window.fs_points_map_popup !== 'function') return;

    const originalPopup = window.fs_points_map_popup;

    window.fs_points_map_popup = (...args) => {
      return openPickupMapWithResolvedCenter(originalPopup, args);
    };

    state.mapPopupWrapped = true;
    window.clearInterval(state.mapPopupWrapTimer);
  };

  const startPickupMapPopupWrap = () => {
    hookLeafletMaps();
    wrapPickupMapPopup();

    if (state.mapPopupWrapped || state.mapPopupWrapTimer) return;

    state.mapPopupWrapTimer = window.setInterval(() => {
      hookLeafletMaps();
      wrapPickupMapPopup();
    }, 250);
  };

  const updateMapCenterFromPosition = (position) => {
    state.mapCenter = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };
  };

  const getPickupAddressElement = () => getPickupSection()?.querySelector(selectors.pickupAddress);

  const getPickupCarrierElement = () => getPickupSection()?.querySelector(selectors.pickupCarrier);

  const getPickupAddressText = () => {
    const addressText = getPickupAddressElement()?.textContent?.trim() || '';
    const carrierText = getPickupCarrierElement()?.textContent?.trim() || '';

    if (!addressText) return '';
    if (!carrierText) return addressText;

    return `${addressText} (${carrierText})`;
  };

  const updateHiddenPickupInputs = () => {
    const pickupAddressInput = getPickupAddressInput();
    const pickupCarrierInput = getPickupCarrierInput();
    const checkoutAddressInput = getCheckoutAddressInput();
    const method = getSelectedMethod();

    if (!pickupAddressInput || !pickupCarrierInput || !checkoutAddressInput) return;

    if (method !== 'pickup_point') {
      pickupAddressInput.value = '';
      pickupCarrierInput.value = '';
      checkoutAddressInput.value = '';
      return;
    }

    pickupAddressInput.value = getPickupAddressText();
    pickupCarrierInput.value = getPickupCarrierElement()?.textContent?.trim() || '';
    checkoutAddressInput.value = pickupAddressInput.value;
  };

  const syncPanelsAndWidget = () => {
    const optionsRoot = getOptionsRoot();
    const pickupSection = getPickupSection();
    const method = getSelectedMethod();
    const isPickupSelected = method === 'pickup_point';

    if (!optionsRoot) return;

    optionsRoot.querySelectorAll(selectors.deliveryPanel).forEach((panel) => {
      panel.toggleAttribute('hidden', panel.dataset.deliveryPanel !== method);
    });

    if (pickupSection) {
      pickupSection.toggleAttribute('hidden', !isPickupSelected);
    }

    updateHiddenPickupInputs();
    updateOptionPrices();
    updateTotals();
    updateFreeShippingProgress();
    updatePickupWidgetTranslations();
  };

  const setError = (message) => {
    const cartErrors = getCartErrors();
    if (!cartErrors) return;
    cartErrors.textContent = message;
  };

  const clearError = () => setError('');

  const setCheckoutButtonLoading = (checkoutButton, isLoading) => {
    if (!checkoutButton) return;

    if (isLoading) {
      checkoutButton.setAttribute('aria-disabled', 'true');
      checkoutButton.disabled = true;
      return;
    }

    checkoutButton.removeAttribute('aria-disabled');
    checkoutButton.disabled = false;
  };

  const persistDeliveryAttributes = async () => {
    const method = getSelectedMethod();
    const pickupAddress = method === 'pickup_point' ? getPickupAddressText() : '';
    const pickupCarrier = method === 'pickup_point' ? getPickupCarrierElement()?.textContent?.trim() || '' : '';
    const signature = JSON.stringify({ method, pickupAddress, pickupCarrier });

    if (signature === state.lastPersistedSignature) return;

    const response = await fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        attributes: {
          delivery_method: method,
          pickup_point_address: pickupAddress,
          pickup_point_carrier: pickupCarrier,
        },
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to save delivery attributes');
    }

    state.lastPersistedSignature = signature;
  };

  const schedulePersist = () => {
    window.clearTimeout(state.persistTimer);
    state.persistTimer = window.setTimeout(() => {
      persistDeliveryAttributes().catch(() => {});
    }, 300);
  };

  const getLiveCart = async () => {
    const response = await fetch(`${routes.cart_url}.js`);
    if (!response.ok) {
      throw new Error('Unable to read live cart');
    }

    return response.json();
  };

  const getPickupPointFromCart = (cart) => {
    const pickupPointValue = cart?.attributes?._pickup_point;
    if (!pickupPointValue) return null;

    try {
      return JSON.parse(pickupPointValue);
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const buildPickupCheckoutUrl = (cart, pickupPoint) => {
    const lineItems = cart.items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
    if (!lineItems) {
      throw new Error('Cart is empty');
    }

    const queryParams = new URLSearchParams();
    const address1 = pickupPoint.providerTitle
      ? `${pickupPoint.address} (${pickupPoint.providerTitle})`
      : pickupPoint.address || '';

    if (address1) {
      queryParams.set('checkout[shipping_address][address1]', address1);
    }
    if (pickupPoint.postCode) {
      queryParams.set('checkout[shipping_address][zip]', pickupPoint.postCode);
    }
    if (pickupPoint.city) {
      queryParams.set('checkout[shipping_address][city]', pickupPoint.city);
    }
    if (pickupPoint.country) {
      queryParams.set('checkout[shipping_address][country]', pickupPoint.country);
    }

    const cartAttributes = {
      ...(cart.attributes || {}),
      delivery_method: 'pickup_point',
      pickup_point_address: address1,
      pickup_point_carrier: pickupPoint.providerTitle || '',
      _pickup_point: cart.attributes?._pickup_point || '',
    };

    Object.entries(cartAttributes).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      queryParams.append(`attributes[${key}]`, value);
    });

    return `${window.shopUrl}${routes.cart_url}/${lineItems}?${queryParams.toString()}`;
  };

  const handleCheckout = async (event) => {
    if (getSelectedMethod() !== 'pickup_point') return;

    event.preventDefault();
    clearError();

    const checkoutButton = event.currentTarget;
    setCheckoutButtonLoading(checkoutButton, true);
    window.clearTimeout(state.checkoutUnlockTimer);
    state.checkoutUnlockTimer = window.setTimeout(() => {
      setCheckoutButtonLoading(checkoutButton, false);
    }, 5000);

    try {
      await persistDeliveryAttributes();
      const cart = await getLiveCart();
      const pickupPoint = getPickupPointFromCart(cart);

      if (!pickupPoint?.address) {
        setError(getPickupRequiredMessage());
        getPickupSection()?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.clearTimeout(state.checkoutUnlockTimer);
        setCheckoutButtonLoading(checkoutButton, false);
        return;
      }

      window.location.href = buildPickupCheckoutUrl(cart, pickupPoint);
    } catch (error) {
      console.error(error);
      window.clearTimeout(state.checkoutUnlockTimer);
      setError(getCheckoutErrorMessage());
      setCheckoutButtonLoading(checkoutButton, false);
    }
  };

  const syncPickupObserver = () => {
    const pickupSection = getPickupSection();

    if (state.observedPickupSection === pickupSection) return;

    state.pickupObserver?.disconnect();
    state.observedPickupSection = pickupSection;

    if (!pickupSection) return;

    state.pickupObserver = new MutationObserver(() => {
      updatePickupWidgetTranslations();
      updateHiddenPickupInputs();
      if (getSelectedMethod() === 'pickup_point') {
        schedulePersist();
      }
    });

    state.pickupObserver.observe(pickupSection, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  const bindCheckoutButton = () => {
    const checkoutButton = document.querySelector(selectors.checkoutButton);
    if (!checkoutButton || checkoutButton.dataset.deliveryBound === 'true') return;

    checkoutButton.dataset.deliveryBound = 'true';
    checkoutButton.addEventListener('click', handleCheckout);
  };

  const syncCartDeliveryOptions = () => {
    state.syncQueued = false;
    const optionsRoot = getOptionsRoot();
    if (!optionsRoot) return;

    startPickupMapPopupWrap();

    syncPanelsAndWidget();
    syncPickupObserver();
    bindCheckoutButton();
  };

  const queueSync = () => {
    if (state.syncQueued) return;
    state.syncQueued = true;

    requestAnimationFrame(() => {
      syncCartDeliveryOptions();
    });
  };

  document.addEventListener('change', (event) => {
    if (!event.target.matches(selectors.deliveryMethod)) return;

    clearError();
    queueSync();
    schedulePersist();
  });

  document.body.addEventListener('cart:refresh', queueSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueSync);
  } else {
    queueSync();
  }
})();
